/**
 * D2 (0.9.41-rc3): PayPal draws its white button's 1px border on the very edge of its own frame. On a screen scaled to 125% or
 * 150% (most Windows laptops) the frame's edges fall between the screen's pixels, and that edge is blended away: the right border
 * looked cut off. This keeps the box PayPal draws in starting and ending on whole screen pixels, whatever the scale, the theme's
 * widths or the window's size.
 */

/**
 * Sizes `slot` to the widest run of whole screen pixels inside its parent's content box, and moves it to start on one. Writes two
 * custom properties the stylesheet reads (`--anudan-align-width`, `--anudan-align-nudge`); without them the slot is 100% wide.
 *
 * @param {HTMLElement} slot
 * @return {void}
 */
export function alignToScreenPixels( slot ) {
	const parent = slot.parentElement;
	const view = slot.ownerDocument.defaultView;

	if ( ! parent || ! view ) {
		return;
	}

	const ratio = view.devicePixelRatio || 1;
	const box = parent.getBoundingClientRect();
	const style = view.getComputedStyle( parent );
	const left = box.left + parseFloat( style.paddingLeft || '0' ) + parseFloat( style.borderLeftWidth || '0' );
	const right = box.right - parseFloat( style.paddingRight || '0' ) - parseFloat( style.borderRightWidth || '0' );

	/*
	 * From 2 screen pixels to a CSS pixel up (phones, most Macs), PayPal's 1-pixel border is at least 2 screen pixels wide and a
	 * blended edge still leaves a solid line, so the frame simply fills the box. Aligning there only cost room: on a Pixel 7 (2.625)
	 * the frame ended 6 pixels short of the box's right side, and at that scale a start both pixels share comes only every 8 CSS
	 * pixels (found on Android in the rc3 checks).
	 */
	if ( ratio >= 2 ) {
		slot.style.setProperty( '--anudan-align-nudge', '0px' );
		slot.style.setProperty( '--anudan-align-width', `${ Math.max( 0, right - left ) }px` );

		return;
	}

	/*
	 * The frame starts on a whole screen pixel, and one a browser can place exactly: WebKit places boxes in 64ths of a CSS pixel,
	 * so at 150% the 74th screen pixel (49⅓ CSS pixels) would land at 49.328 and blend the edge again.
	 */
	let device = Math.ceil( left * ratio - 0.001 );

	for ( let tries = 0; tries < 8; tries += 1 ) {
		if ( Math.abs( ( device / ratio ) * 64 - Math.round( ( device / ratio ) * 64 ) ) < 0.0001 ) {
			break;
		}

		device += 1;
	}

	const start = Math.abs( ( device / ratio ) * 64 - Math.round( ( device / ratio ) * 64 ) ) < 0.0001 ? device / ratio : Math.ceil( left * ratio - 0.001 ) / ratio;
	let width = Math.floor( right - start + 0.001 );

	/*
	 * Inside its frame PayPal lays the button out in whole CSS pixels, so the button's right edge is on a whole screen pixel only
	 * when the frame's width is whole in CSS pixels and in screen pixels both: a multiple of 4 at 125%, of 2 at 150%. The widest
	 * such width is used, a few pixels narrower at most; a scale with no such width nearby keeps the whole CSS width.
	 */
	for ( let tries = 0; tries < 20 && width > 0; tries += 1 ) {
		if ( Math.abs( width * ratio - Math.round( width * ratio ) ) < 0.01 ) {
			break;
		}

		width -= 1;
	}

	if ( width <= 0 || Math.abs( width * ratio - Math.round( width * ratio ) ) >= 0.01 ) {
		width = Math.floor( right - start + 0.001 );
	}

	if ( width <= 0 ) {
		return;
	}

	slot.style.setProperty( '--anudan-align-nudge', `${ start - left }px` );
	slot.style.setProperty( '--anudan-align-width', `${ width }px` );
}

/**
 * Keeps `slot` aligned while the page changes: the box resizing (a theme's columns, a scrollbar coming or going, the phone
 * turned) and the screen's scale changing (the window dragged to another screen, the page zoomed).
 *
 * @param {HTMLElement} slot
 * @return {() => void} Stops watching.
 */
export function keepAlignedToScreenPixels( slot ) {
	const view = slot.ownerDocument.defaultView;
	const align = () => alignToScreenPixels( slot );
	const observer = view && 'function' === typeof view.ResizeObserver ? new view.ResizeObserver( align ) : null;
	let scale = null;

	const watchScale = () => {
		if ( ! view || 'function' !== typeof view.matchMedia ) {
			return;
		}

		scale = view.matchMedia( `(resolution: ${ view.devicePixelRatio || 1 }dppx)` );
		scale.addEventListener( 'change', onScale, { once: true } );
	};
	const onScale = () => {
		align();
		watchScale();
	};

	align();

	if ( observer && slot.parentElement ) {
		observer.observe( slot.parentElement );
		// A scrollbar coming or going moves a centred box without resizing it; the page's own width changes.
		observer.observe( slot.ownerDocument.documentElement );
	}

	if ( view ) {
		view.addEventListener( 'resize', align );
	}

	watchScale();

	return () => {
		if ( observer ) {
			observer.disconnect();
		}

		if ( view ) {
			view.removeEventListener( 'resize', align );
		}

		if ( scale ) {
			scale.removeEventListener( 'change', onScale );
		}
	};
}
